import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  IconButton,
  Popover,
  Tooltip,
} from '@mui/material';
import X from '@mui/icons-material/X';
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import YouTube from '@mui/icons-material/YouTube';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { useChat } from '@/hooks/useChat';
import { getBallotActionState, type PendingBallotAction } from './ballotActionState';
import { primitiveColors, semanticColors, semanticGradients, BORDER_RADIUS } from '@/theme/tokens';
import { gradientBorderBackground } from '@/theme/utils';
import useAnalytics from '@/hooks/useAnalytics';
import useLikedCandidates from '@/hooks/useLikedCandidates';
import { isSafeHttpUrl } from '@/utils/electionOfficeLinks';
import type { CandidateBallotpediaBlock } from '@/types/ballotpedia';
import { getCandidateStatusDisplay } from '@/utils/candidateStatus';
import { candidateKey } from './candidateKey';
import { isolate } from '@/utils/bidi';
import { useDirection } from '@/hooks/useDirection';

/**
 * A campaign email safe to compose a `mailto:` URI from.
 *
 * Everything here is third-party data, so the address is validated rather than
 * trusted: a value carrying `?` would let the source populate the subject or
 * body of the voter's outgoing mail, and one carrying a newline could add
 * headers. Requiring a plain single-address shape rejects both, and drops
 * malformed rows instead of rendering a chip that opens a broken draft.
 */
const safeMailtoAddress = (email: string | undefined): string | undefined => {
  const value = email?.trim();
  return value && /^[^\s@,;:<>()[\]\\"?&#]+@[^\s@,;:<>()[\]\\"?&#]+\.[a-z]{2,}$/i.test(value)
    ? value
    : undefined;
};

export interface Candidate {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  partyAffiliation?: string[] | null;
  isIncumbent?: boolean;
  runningMate?: {
    id: string;
    fullName: string;
    firstName: string;
    lastName: string;
    partyAffiliation: string[];
    title: string;
  };
  runningMateFullName?: string;
  runningMateTitle?: string;
  /** The other half of a Governor / Lt. Governor ticket shown as two contests;
      the pick key fields only. Hearting either member hearts both. */
  ticketMate?: { id?: string; ballotpediaUrl?: string; fullName?: string };
  contact: {
    campaign: {
      email?: string;
      phone?: string;
      website?: string;
      facebook?: string;
      twitter?: string;
      instagram?: string;
      youtube?: string;
    };
  };
  /** The candidate's Ballotpedia page, as Democracy Works carries it — and as
      the backend writes it onto candidates in Ballotpedia-only local contests
      that Democracy Works never returned. Read alongside
      `bp.ballotpedia_url`, preferring whichever is present: enrichment prunes
      `bp.ballotpedia_url` whenever this field already holds the same URL, so
      for many candidates this is the only copy. */
  ballotpediaUrl?: string;
  /** Ballotpedia enrichment — endorsements, key messages, survey summary.
      Present when the backend matched this candidate; see
      `@/types/ballotpedia`. Nothing renders it yet. */
  bp?: CandidateBallotpediaBlock;
}

interface BallotCandidateProps {
  candidate: Candidate;
  isLast: boolean;
  isMobile: boolean;
  onCloseBallot?: () => void;
  transparentBackground?: boolean;
  /** Route hearts to a specific ballot record instead of the live
      liked-candidates store (used when viewing a record from history). */
  heartsOverride?: {
    isLiked: (id: string) => boolean;
    toggle: (id: string) => void;
    setPicks: (ids: string[], picked: boolean) => void;
  } | null;
  /** Freeze the heart (past-election records are read-only). */
  heartsReadOnly?: boolean;
  /** The contest this row sits in — anchors "Full profile" to the tapped race. */
  contestId?: string;
}

// Memoized — see comment at the bottom export for rationale and prop
// stability requirements.
const BallotCandidateImpl: React.FC<BallotCandidateProps> = ({
  candidate,
  isLast,
  isMobile,
  onCloseBallot,
  transparentBackground = false,
  heartsOverride = null,
  heartsReadOnly = false,
  contestId,
}) => {
  const { t } = useTranslation('ballot');
  const dir = useDirection();
  const { sendChat, isProcessing } = useChat();
  const analytics = useAnalytics();
  const { isLiked, toggleLike, setPicks } = useLikedCandidates();
  const [pendingAction, setPendingAction] = useState<PendingBallotAction | null>(null);

  useEffect(() => {
    if (!isProcessing) {
      setPendingAction(null);
    }
  }, [isProcessing]);
  // Key the pick on candidateKey (id ?? url ?? name) so id-less Ballotpedia-only
  // candidates don't all share one `undefined` key and toggle as one.
  const key = candidateKey(candidate);
  // A Governor / Lt. Governor ticket split across two contests: each row shows
  // its own heart, and a tap sets both members to the new state.
  const ticketMateKey = candidate.ticketMate ? candidateKey(candidate.ticketMate) : undefined;
  const ticketMateHintId = useId();
  // The partner's heart sits in another race, so announce that it changed too.
  const [ticketMateAnnouncement, setTicketMateAnnouncement] = useState('');
  const liked = heartsOverride ? heartsOverride.isLiked(key) : isLiked(key);

  // Candidate status affordance — shown only when Ballotpedia reports something
  // other than plainly on the ballot (write-in, withdrew, disqualified, etc.).
  const candidateStatus = getCandidateStatusDisplay(candidate.bp);
  // Write-in shows an inline "write-in" tag after the name (like incumbent);
  // every other flagged status shows the info icon + popover instead.
  const isWriteIn = candidateStatus.kind === 'writeIn';
  const showStatusIcon = candidateStatus.showBadge && !isWriteIn;
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const handleStatusClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // Keep the AccordionSummary from toggling when the info icon is clicked.
      e.stopPropagation();
      setStatusAnchor(e.currentTarget);
      analytics.track('Sample Ballot Candidate Status Opened', {
        status: candidateStatus.kind,
      });
    },
    [analytics, candidateStatus.kind]
  );

  const handleToggleLike = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the AccordionSummary from toggling expand when the heart is clicked.
      e.stopPropagation();
      if (heartsReadOnly) return;
      let next: boolean;
      if (ticketMateKey) {
        next = !(heartsOverride ? heartsOverride.isLiked(key) : isLiked(key));
        (heartsOverride ? heartsOverride.setPicks : setPicks)([key, ticketMateKey], next);
        setTicketMateAnnouncement(
          t(next ? 'candidate.ticketMatePicked' : 'candidate.ticketMateUnpicked', {
            mate: candidate.ticketMate?.fullName,
          })
        );
      } else if (heartsOverride) {
        next = !heartsOverride.isLiked(key);
        heartsOverride.toggle(key);
      } else {
        next = toggleLike(key);
      }
      analytics.track('Sample Ballot Candidate Liked', {
        candidateId: candidate.id,
        liked: next,
        fromRecord: Boolean(heartsOverride),
      });
    },
    [
      analytics,
      candidate.id,
      candidate.ticketMate?.fullName,
      t,
      key,
      ticketMateKey,
      isLiked,
      setPicks,
      toggleLike,
      heartsOverride,
      heartsReadOnly,
    ]
  );

  // Two sources carry this URL and each is the only one for a large slice of
  // candidates, so read both: enrichment prunes `bp.ballotpedia_url` when the
  // Democracy Works record already has `ballotpediaUrl`, and candidates in
  // Ballotpedia-only local contests get `ballotpediaUrl` with no `bp` block.
  // Scheme-checked before it reaches an href, matching the mobile guard — this
  // is third-party data, and only http(s) belongs in a link.
  const rawBallotpediaUrl = candidate.bp?.ballotpedia_url ?? candidate.ballotpediaUrl;
  const ballotpediaUrl =
    rawBallotpediaUrl && isSafeHttpUrl(rawBallotpediaUrl) ? rawBallotpediaUrl : undefined;
  const emailAddress = safeMailtoAddress(candidate.contact?.campaign?.email);
  // Rendered as its own chip on row one, beside Full profile.
  const websiteUrl = candidate.contact?.campaign?.website?.trim() || undefined;

  // Gate on the same values the chips render, so a row never expands to an
  // empty strip because a channel held whitespace or a malformed address.
  const hasSocials = useMemo(() => {
    return (
      candidate.contact?.campaign?.website?.trim() ||
      emailAddress ||
      candidate.contact?.campaign?.twitter?.trim() ||
      candidate.contact?.campaign?.facebook?.trim() ||
      candidate.contact?.campaign?.instagram?.trim() ||
      candidate.contact?.campaign?.youtube?.trim() ||
      ballotpediaUrl
    );
  }, [
    emailAddress,
    candidate.contact?.campaign?.facebook,
    candidate.contact?.campaign?.instagram,
    candidate.contact?.campaign?.twitter,
    candidate.contact?.campaign?.website,
    candidate.contact?.campaign?.youtube,
    ballotpediaUrl,
  ]);

  const handleClickFullProfile = useCallback(() => {
    if (isProcessing || pendingAction) return;
    setPendingAction({ kind: 'fullProfile', id: key });
    analytics.track('Sample Ballot Candidate Full Profile Clicked', {
      candidateId: candidate.id,
    });
    // Same visible text as before; the ref tells the backend which row was
    // tapped so it states this race's own counts instead of re-deriving them.
    sendChat(t('candidate.fullProfileQuery', { name: candidate.fullName }), {
      ballotRef: { kind: 'candidate', contest_id: contestId, candidate_name: candidate.fullName },
    });
    if (isMobile && onCloseBallot) {
      onCloseBallot();
    }
  }, [
    analytics,
    candidate.id,
    candidate.fullName,
    contestId,
    isMobile,
    isProcessing,
    key,
    onCloseBallot,
    pendingAction,
    sendChat,
    t,
  ]);

  const fullProfileActionState = getBallotActionState({
    kind: 'fullProfile',
    id: key,
    pendingAction,
    isProcessing,
  });

  return (
    <>
      {/* Outside the AccordionSummary button, so neither joins its name. */}
      {ticketMateKey && (
        <>
          <Box component="span" id={ticketMateHintId} sx={visuallyHidden}>
            {t('candidate.ticketMateHint', { mate: candidate.ticketMate?.fullName })}
          </Box>
          <Box component="span" role="status" sx={visuallyHidden}>
            {ticketMateAnnouncement}
          </Box>
        </>
      )}
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          '&.Mui-expanded': {
            margin: 0,
            backgroundColor: transparentBackground ? 'transparent' : semanticColors.surface,
          },
          borderBottom: isLast ? 'none' : `1px solid ${primitiveColors.black}`,
          backgroundColor: transparentBackground ? 'transparent' : semanticColors.surface,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 3,
            py: 1,
            minHeight: 'unset',
            '&.Mui-expanded': { minHeight: 'unset' },
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              margin: 0,
              // The chevron is pinned at physical right: 8. In LTR the party's
              // marginRight clears it; in RTL the party flips to the left, so the
              // name would collide with the chevron — reserve the space here, as
              // the officeholder rows do.
              ...(dir === 'rtl' ? { pr: 3 } : {}),
              '&.Mui-expanded': {
                margin: 0,
              },
            },
            '& .MuiAccordionSummary-expandIconWrapper': (theme) => ({
              position: 'absolute',
              right: theme.spacing(1),
              top: '50%',
              transform: 'translateY(-50%)',
              '&.Mui-expanded': {
                transform: 'translateY(-50%) rotate(180deg)',
              },
            }),
          }}
        >
          {/* Like-heart — IconButton's hit area is enlarged to cover the empty strip
            on the left of the row, while the icon itself stays in its original spot.
            ``component="div"`` because this IconButton is rendered inside the
            AccordionSummary (which is already a ``<button>``); nested
            ``<button>``s are invalid HTML and trigger React's dev-mode
            validateDOMNesting on every BallotCandidate render — at N candidates
            per ballot that single warning was producing the bulk of the
            "Run console task" cost in perf traces. ``role="button"`` keeps
            screen-reader semantics; ``onClick`` + ``aria-pressed`` already
            handle the toggle interaction. */}
          <IconButton
            component="div"
            role="button"
            tabIndex={heartsReadOnly ? -1 : 0}
            onClick={handleToggleLike}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleToggleLike(e as unknown as React.MouseEvent<HTMLElement>);
              }
            }}
            aria-label={
              heartsReadOnly
                ? t('candidate.likeReadOnlyAriaLabel', { name: candidate.fullName })
                : liked
                  ? t('candidate.removeLikeAriaLabel', { name: candidate.fullName })
                  : t('candidate.likeAriaLabel', { name: candidate.fullName })
            }
            aria-pressed={liked}
            aria-disabled={heartsReadOnly}
            aria-describedby={ticketMateKey && !heartsReadOnly ? ticketMateHintId : undefined}
            sx={{
              position: 'absolute',
              // -32 sits the button flush with the AccordionDetails' px:1 inner
              // padding (8px) — i.e. extends 8px past the AccordionSummary's
              // own left padding (24px) and stops at the contest container edge.
              left: '-32px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '32px',
              height: '32px',
              p: 0,
              // Override MUI IconButton's default `border-radius: 50%`.
              // Modern browsers do shape-aware hit-testing for border-radius, so
              // the default 50% actually clips the click target to the inscribed
              // circle — corner clicks inside the 32×32 box don't register. Setting
              // to 0 restores the full square as the click target. (The ripple
              // animation still renders as a circle from the click point; it's now
              // clipped by the square boundary instead of by a circular parent.)
              borderRadius: 0,
              color: liked ? primitiveColors.red4 : semanticColors.textMediumEmphasis,
              zIndex: 1,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            {liked ? (
              <FavoriteIcon sx={{ fontSize: { lg: 16, xs: 14 } }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: { lg: 16, xs: 14 } }} />
            )}
          </IconButton>

          {/* Candidate name + status affordance, grouped so the info icon hugs
            the name (the summary content is a space-between flex row). ``flex``
            lives here (not the inner name) so the group grows to fill the row and
            keeps the party label flush-right on mobile. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              flex: { xs: 1, lg: '0 1 auto' },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: { lg: 16, xs: 14 },
                fontWeight: 700,
                lineHeight: 1.1,
                color: semanticColors.textHighEmphasis,
                // Italicize any flagged candidate (write-in, withdrew-still-on-
                // ballot, candidacy declared) so the row reads as an exception.
                fontStyle: candidateStatus.showBadge ? 'italic' : 'normal',
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {dir === 'rtl' ? isolate(candidate.fullName) : candidate.fullName}
              {candidate.isIncumbent && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: { lg: 14, xs: 12 },
                    fontWeight: 400,
                    lineHeight: 1.375,
                    color: semanticColors.textMediumEmphasis,
                  }}
                >
                  {t('candidate.incumbent')}
                </Typography>
              )}
              {isWriteIn && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: { lg: 14, xs: 12 },
                    fontWeight: 400,
                    lineHeight: 1.375,
                    color: semanticColors.textMediumEmphasis,
                  }}
                >
                  {t('candidate.writeIn')}
                </Typography>
              )}
            </Typography>
            {showStatusIcon && (
              <>
                <IconButton
                  // component="div" + role="button" avoids a <button> nested in the
                  // AccordionSummary <button> (invalid HTML) — same as the heart.
                  component="div"
                  role="button"
                  tabIndex={0}
                  size="small"
                  onClick={handleStatusClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStatusClick(e as unknown as React.MouseEvent<HTMLElement>);
                    }
                  }}
                  aria-label={t('candidateStatus.infoAriaLabel', {
                    name: candidate.fullName,
                    status: t(`candidateStatus.${candidateStatus.kind}.label`),
                  })}
                  aria-haspopup="dialog"
                  aria-expanded={Boolean(statusAnchor)}
                  aria-controls={statusAnchor ? `candidate-status-popover-${key}` : undefined}
                  sx={{
                    p: 0.25,
                    flexShrink: 0,
                    color: semanticColors.textMediumEmphasis,
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: { lg: 16, xs: 14 } }} />
                </IconButton>
                <Popover
                  open={Boolean(statusAnchor)}
                  anchorEl={statusAnchor}
                  onClose={() => setStatusAnchor(null)}
                  // Swallow the click so it can't bubble through the React portal
                  // and toggle the accordion (matches the pending-verification popover).
                  onClick={(e) => e.stopPropagation()}
                  disableAutoFocus
                  disableEnforceFocus
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  sx={{ zIndex: 1850 }}
                  slotProps={{
                    paper: {
                      // role=status + aria-live announces the label/meaning on open
                      // (focus stays on the trigger, so nothing else would read it).
                      id: `candidate-status-popover-${key}`,
                      role: 'status',
                      'aria-live': 'polite',
                      sx: { p: 2, maxWidth: 280, borderRadius: BORDER_RADIUS },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      mb: 0.5,
                      color: semanticColors.textHighEmphasis,
                    }}
                  >
                    {t(`candidateStatus.${candidateStatus.kind}.label`)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: semanticColors.textMediumEmphasis,
                    }}
                  >
                    {t(`candidateStatus.${candidateStatus.kind}.meaning`)}
                  </Typography>
                </Popover>
              </>
            )}
          </Box>

          {/* Party affiliation */}
          <Typography
            component="span"
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: { lg: 14, xs: 12 },
              fontWeight: 400,
              lineHeight: 1.375,
              color: semanticColors.textMediumEmphasis,
              // Italicize the party for flagged candidates, matching the name.
              fontStyle: candidateStatus.showBadge ? 'italic' : 'normal',
              // Mobile: sized to its text (capped at 45%) so it sits flush against
              // the chevron instead of being squeezed into a mid-word wrap.
              // Desktop keeps the original flex sizing and wrapping.
              flexShrink: { xs: 0, lg: 1 },
              maxWidth: { xs: '45%', lg: 'none' },
              // Content ends 24px from the edge and the chevron starts at 32px
              // (the desktop dashboard theme uses MUI's 8px spacing too), so 12px
              // leaves a 4px gap to the arrow on every breakpoint.
              marginRight: '12px',
              minWidth: 0,
              textAlign: 'right',
              overflowWrap: { xs: 'break-word', lg: 'anywhere' },
            }}
          >
            {(candidate.partyAffiliation ?? []).join(', ')}
          </Typography>
        </AccordionSummary>

        <AccordionDetails
          sx={{ pl: 3, pr: { xs: 1, lg: 3 }, pt: 0.75, pb: 1, backgroundColor: 'inherit' }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, lg: 1 },
              flexDirection: { xs: 'column', lg: 'row' },
            }}
          >
            {/* Full Profile + Social Links */}
            <Box sx={{ flex: '0 0 100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 0.5, lg: 0.75 },
                  alignItems: 'center',
                }}
              >
                {/* Full profile + official website lead; the icon links flow after
                  them on the same line and wrap only when the row runs out. */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 0.5, lg: 0.75 },
                    alignItems: 'center',
                  }}
                >
                  {/* Full Profile chip always first */}
                  <Chip
                    label={
                      fullProfileActionState.isThisActionPending
                        ? t('candidate.gettingProfile')
                        : t('candidate.fullProfile')
                    }
                    onClick={handleClickFullProfile}
                    disabled={fullProfileActionState.isDisabled}
                    size="small"
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 11,
                      letterSpacing: '0.19px',
                      borderRadius: BORDER_RADIUS,
                      border: '2px solid transparent',
                      background: gradientBorderBackground(
                        semanticColors.background,
                        semanticGradients.primary
                      ),
                      backgroundColor: semanticColors.chatBotMessage,
                      width: 'fit-content',
                      '& .MuiChip-label': { px: { xs: 1, lg: 1.5 } },
                    }}
                  />
                  {websiteUrl && (
                    <Chip
                      label={t('candidate.officialWebsite')}
                      component="a"
                      href={websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      variant="outlined"
                      size="small"
                      sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11,
                        fontWeight: 400,
                        letterSpacing: '0.19px',
                        border: `2px solid ${semanticColors.websiteLink}`,
                        borderRadius: BORDER_RADIUS,
                        '& .MuiChip-label': { px: { xs: 0.75, lg: 1 } },
                      }}
                    />
                  )}
                </Box>
                {/* Social links */}
                {hasSocials &&
                  [
                    {
                      // Often a down-ballot candidate's only contact. Bordered
                      // envelope icon, matching the officeholder contact row.
                      label: t('candidate.email'),
                      url: emailAddress ? `mailto:${emailAddress}` : undefined,
                      icon: (
                        <EnvelopeIcon
                          style={{
                            width: 14,
                            height: 14,
                            color: semanticColors.brandPrimaryHover,
                          }}
                        />
                      ),
                      borderColor: semanticColors.brandPrimaryHover,
                    },
                    {
                      // Ballotpedia "BP" button — same styling as the officeholder one.
                      label: t('candidate.ballotpediaProfile'),
                      url: ballotpediaUrl,
                      icon: (
                        <Box
                          aria-hidden
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 14,
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1,
                            letterSpacing: 0.2,
                          }}
                        >
                          <Box component="span" sx={{ color: semanticColors.ballotpediaBlue }}>
                            B
                          </Box>
                          <Box component="span" sx={{ color: semanticColors.ballotpediaOrange }}>
                            P
                          </Box>
                        </Box>
                      ),
                      borderColor: semanticColors.ballotpediaBlue,
                    },
                    {
                      label: t('candidate.xTwitter'),
                      url: candidate.contact?.campaign?.twitter,
                      icon: <X sx={{ fontSize: 14, color: '#000000' }} />,
                      borderColor: '#000000',
                    },
                    {
                      label: t('candidate.instagram'),
                      url: candidate.contact?.campaign?.instagram,
                      icon: <Instagram sx={{ fontSize: 14, color: '#E1306C' }} />,
                      borderColor: '#E1306C',
                    },
                    {
                      label: t('candidate.facebook'),
                      url: candidate.contact?.campaign?.facebook,
                      icon: <Facebook sx={{ fontSize: 14, color: '#1877F2' }} />,
                      borderColor: '#1877F2',
                    },
                    {
                      label: t('candidate.youtube'),
                      url: candidate.contact?.campaign?.youtube,
                      icon: <YouTube sx={{ fontSize: 14, color: '#FF0000' }} />,
                      borderColor: '#FF0000',
                    },
                  ]
                    .filter(
                      (
                        source
                      ): source is {
                        label: string;
                        url: string;
                        icon: React.ReactNode | null;
                        borderColor?: string;
                      } => !!source.url
                    )
                    .map((source) =>
                      source.icon ? (
                        <Tooltip key={source.url} title={source.label}>
                          <IconButton
                            component="a"
                            href={source.url}
                            // `mailto:` hands off to the mail client, so a new tab
                            // would only flash an empty one (matches the chip path).
                            {...(source.url?.startsWith('mailto:')
                              ? {}
                              : { target: '_blank', rel: 'noopener noreferrer' })}
                            size="small"
                            aria-label={source.label}
                            sx={{
                              border: `2px solid ${source.borderColor}`,
                              borderRadius: BORDER_RADIUS,
                              p: { xs: '1px', lg: '2px' },
                            }}
                          >
                            {source.icon}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Chip
                          key={source.url}
                          label={source.label}
                          component="a"
                          href={source.url}
                          // `mailto:` hands off to the mail client, so a new tab
                          // would only flash an empty one.
                          {...(source.url.startsWith('mailto:')
                            ? {}
                            : { target: '_blank', rel: 'noopener noreferrer' })}
                          clickable
                          variant="outlined"
                          size="small"
                          sx={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 11,
                            fontWeight: 400,
                            letterSpacing: '0.19px',
                            border: `2px solid ${semanticColors.websiteLink}`,
                            borderRadius: BORDER_RADIUS,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      )
                    )}
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

// Memoized so that BallotCandidate doesn't re-render when only the
// BallotSheet's drag-frac state changes (which happens every touchmove
// frame). The BallotSheetProvider's context value changes on every
// drag, and SampleBallotView is a context consumer — so the parent
// re-renders constantly during a swipe. Without this memo, every
// candidate's body executes for each one of those frames, dominating
// CPU time and causing visible stutter.
//
// Memo correctness requires the candidate's props to be referentially
// stable across renders when the *candidate itself* hasn't changed:
//   - ``candidate`` — comes from ballot data; only changes when the
//     ballot updates, not during drag.
//   - ``isLast`` / ``isMobile`` / ``transparentBackground`` — primitives.
//   - ``onCloseBallot`` — must be ``useCallback``-d at the call site.
//     ``handleCloseBallot`` in ``SampleBallotView`` is wrapped to
//     guarantee this.
export const BallotCandidate = React.memo(BallotCandidateImpl);
